"use client";

import Link from "next/link";
import { ArrowRight, Gauge, Puzzle, Users, X } from "lucide-react";
import { useState } from "react";

type MinecraftPlan = {
  id: string;
  name: string;
  price: string;
  specs: string[];
};

type Recommendation = {
  plan: MinecraftPlan;
  requiredRamGb: number;
  exceedsLargestPlan: boolean;
};

function clampInteger(value: number, min: number, max: number) {
  if (!Number.isFinite(value)) return min;
  return Math.min(max, Math.max(min, Math.round(value)));
}

function getPlanRamGb(plan: MinecraftPlan) {
  const ramSpec = plan.specs.find((spec) => /RAM/i.test(spec));
  const match = ramSpec?.match(/(\d+(?:\.\d+)?)\s*GB/i);
  return match ? Number(match[1]) : 0;
}

export default function MinecraftPlanRecommender({
  plans,
}: {
  plans: MinecraftPlan[];
}) {
  const [open, setOpen] = useState(false);
  const [players, setPlayers] = useState(10);
  const [addons, setAddons] = useState(10);
  const [recommendation, setRecommendation] =
    useState<Recommendation | null>(null);

  function recommendPlan(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();

    const normalizedPlayers = clampInteger(players, 1, 200);
    const normalizedAddons = clampInteger(addons, 0, 300);
    const requiredRamGb = Math.max(
      1,
      Math.ceil((0.75 + normalizedPlayers * 0.1 + normalizedAddons * 0.07) * 1.15),
    );
    const sortedPlans = [...plans].sort(
      (left, right) => getPlanRamGb(left) - getPlanRamGb(right),
    );
    const matchedPlan = sortedPlans.find(
      (plan) => getPlanRamGb(plan) >= requiredRamGb,
    );
    const plan = matchedPlan ?? sortedPlans.at(-1);

    if (!plan) return;

    setPlayers(normalizedPlayers);
    setAddons(normalizedAddons);
    setRecommendation({
      plan,
      requiredRamGb,
      exceedsLargestPlan: !matchedPlan,
    });
  }

  return (
    <div className="mb-12">
      <button
        type="button"
        onClick={() => setOpen((current) => !current)}
        aria-expanded={open}
        className="inline-flex min-h-12 items-center gap-2 rounded-lg bg-violet-500 px-5 text-sm font-bold text-white transition hover:bg-violet-400"
      >
        <Gauge size={18} />
        내 마크 서버 사양 추천받기
      </button>

      {open ? (
        <div className="mt-5 overflow-hidden border-y border-[#e9e7ed] bg-[#faf9fc] lg:grid lg:grid-cols-[1fr_0.85fr]">
          <form className="px-5 py-7 md:px-8" onSubmit={recommendPlan}>
            <div className="flex items-start justify-between gap-5">
              <div>
                <p className="text-xs font-bold tracking-[0.18em] text-violet-300">
                  SERVER MATCH
                </p>
                <h3 className="mt-2 text-2xl font-black">사용 환경 입력</h3>
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label="사양 추천기 닫기"
                className="grid size-10 shrink-0 place-items-center rounded-lg border border-[#e9e7ed] text-slate-500 transition hover:bg-white"
              >
                <X size={18} />
              </button>
            </div>

            <div className="mt-7 grid gap-5 sm:grid-cols-2">
              <label className="block">
                <span className="flex items-center gap-2 text-sm font-bold">
                  <Users size={17} className="text-violet-200" />
                  최대 동시 접속 인원
                </span>
                <div className="mt-2 flex items-center border-b border-[#d8d4df] bg-white px-4 focus-within:border-violet-400">
                  <input
                    type="number"
                    min={1}
                    max={200}
                    value={players}
                    onChange={(event) => setPlayers(Number(event.target.value))}
                    className="min-h-14 w-full bg-transparent text-xl font-black outline-none"
                  />
                  <span className="text-sm font-semibold text-slate-500">명</span>
                </div>
              </label>

              <label className="block">
                <span className="flex items-center gap-2 text-sm font-bold">
                  <Puzzle size={17} className="text-violet-200" />
                  플러그인·모드 개수
                </span>
                <div className="mt-2 flex items-center border-b border-[#d8d4df] bg-white px-4 focus-within:border-violet-400">
                  <input
                    type="number"
                    min={0}
                    max={300}
                    value={addons}
                    onChange={(event) => setAddons(Number(event.target.value))}
                    className="min-h-14 w-full bg-transparent text-xl font-black outline-none"
                  />
                  <span className="text-sm font-semibold text-slate-500">개</span>
                </div>
              </label>
            </div>

            <p className="mt-4 text-sm leading-6 text-slate-500">
              대형 모드팩, 넓은 월드 또는 자동화 장치가 많은 서버는 한 단계 높은
              플랜이 더 안정적일 수 있습니다.
            </p>

            <button
              type="submit"
              className="mt-6 inline-flex min-h-12 items-center gap-2 rounded-lg bg-violet-500 px-5 text-sm font-bold text-white transition hover:bg-violet-400"
            >
              맞는 요금제 확인
              <ArrowRight size={17} />
            </button>
          </form>

          <div className="border-t border-[#e9e7ed] px-5 py-7 md:px-8 lg:border-l lg:border-t-0">
            {recommendation ? (
              <div className="flex h-full flex-col justify-between">
                <div>
                  <p className="text-xs font-bold tracking-[0.18em] text-violet-300">
                    RECOMMENDED
                  </p>
                  <p className="mt-3 text-sm text-slate-500">
                    예상 필요 메모리 {recommendation.requiredRamGb}GB 기준
                  </p>
                  <h3 className="mt-2 text-3xl font-black">
                    {recommendation.plan.name}
                  </h3>
                  <p className="mt-2 text-xl font-black text-violet-200">
                    {recommendation.plan.price}
                  </p>
                  <ul className="mt-5 space-y-2 text-sm text-slate-400">
                    {recommendation.plan.specs.map((spec) => (
                      <li key={spec}>{spec}</li>
                    ))}
                  </ul>
                  {recommendation.exceedsLargestPlan ? (
                    <p className="mt-5 text-sm font-semibold leading-6 text-orange-100">
                      현재 최대 플랜보다 높은 사양이 예상됩니다. 우선 이 플랜을
                      기준으로 고객센터에 맞춤 구성을 문의해주세요.
                    </p>
                  ) : null}
                </div>

                <Link
                  href={`/order?service=mc&plan=${recommendation.plan.id}`}
                  className="mt-7 inline-flex min-h-12 items-center justify-center gap-2 rounded-lg bg-violet-500 px-5 text-sm font-bold text-white hover:bg-violet-400"
                >
                  {recommendation.exceedsLargestPlan ? "이 플랜으로 상담하기" : "추천 플랜 주문하기"}
                  <ArrowRight size={17} />
                </Link>
              </div>
            ) : (
              <div className="flex h-full min-h-48 flex-col justify-center">
                <p className="text-sm font-bold text-slate-500">추천 결과</p>
                <p className="mt-3 max-w-sm text-lg font-bold leading-7">
                  왼쪽에 예상 사용량을 입력하면 현재 판매 중인 플랜 중 가장 알맞은
                  구성을 보여드립니다.
                </p>
              </div>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}
